/**
 * Bucle de captura (004 · US1 · T028; research R-2 y R-6).
 *
 * Una vuelta por **fotograma real** de la cámara con `requestVideoFrameCallback` —no
 * `setInterval`—, así no se decodifica dos veces el mismo fotograma ni se gasta batería en trabajo
 * que nadie pidió. Donde no exista (Firefox), cae a `requestAnimationFrame`.
 *
 * En cada vuelta: se recorta el ROI central del tamaño **real** del track y se **reduce a 400×400
 * por promedio de área** (`roi.ts`) antes de transferir la imagen al worker. La reducción es
 * obligatoria: sin ella la decodificación de QR fotografiados de una pantalla es 0 % (T001).
 *
 * **El tamaño del recorte ya no es fijo** (T096): lo decide `roi-controller.ts` vuelta a vuelta —
 * barre mientras busca y se engancha al tamaño medido del símbolo en cuanto lee algo. Cada lectura
 * sale etiquetada con el ROI que la produjo (`roiFraction`, `roiSidePx`), y cada cambio de recorte
 * se anuncia por `onRoi` **porque el marco de enfoque que ve el trabajador se dibuja desde ahí**:
 * si el recorte se ajusta y el marco no, la persona encuadra según una guía que miente.
 *
 * El **instante de captura** se toma con `performance.now()` **antes** de dibujar, no cuando
 * responde el worker: es un reloj monotónico y lo que se reporta son tiempos **relativos** al
 * primer frame observado (R-6, constitución IV). El reloj de pared del teléfono no entra nunca.
 */
import {
  centerRoi,
  DECODER_INPUT_SIDE,
  drawReducedRoi,
  type RoiContext,
  type ScanRoi,
} from './roi';
import { createRoiController, type RoiController } from './roi-controller';

import type { Decoder } from './decoder';

/** Una lectura: el texto exacto del QR y cuándo se capturó el fotograma que lo traía. */
export interface ScanRead {
  text: string;
  /** `performance.now()` del momento de la captura (monotónico, en ms). */
  capturedAtMs: number;
  /** Lado del símbolo en la imagen reducida; `null` si el decodificador no lo informó. */
  symbolSidePx: number | null;
  /** Milisegundos que costó `readBarcodes` (telemetría de rendimiento). */
  decodeMs: number;
  /** Fracción del lado menor del fotograma con la que se capturó **esta** lectura (T096). */
  roiFraction: number;
  /** Lado del recorte, en píxeles de cámara, con el que se capturó esta lectura. */
  roiSidePx: number;
}

export interface ScannerOptions {
  /** Elemento con el stream ya asignado y reproduciendo. */
  video: HTMLVideoElement;
  decoder: Decoder;
  /** Se llama por cada lectura, en orden de captura. */
  onRead: (read: ScanRead) => void;
  /** Reloj monotónico; por defecto `performance.now()`. */
  now?: () => number;
  /** Lado de la imagen que entra al decodificador; por defecto 400 (no tocar sin volver a medir). */
  side?: number;
  /**
   * Fija el recorte y desactiva el barrido/enganche de T096. Interruptor de emergencia y atajo de
   * pruebas; en producción **no se pasa** y decide `roi-controller.ts`.
   */
  roiFraction?: number;
  /** Controlador del recorte; por defecto uno nuevo (T096). */
  roiController?: RoiController;
  /**
   * Se llama cada vez que cambia el ROI vigente (y en la primera vuelta con fotograma). **Es lo
   * que alimenta el marco de enfoque**: dibujarlo de otra fuente lo desincroniza del recorte real.
   */
  onRoi?: (roi: ScanRoi) => void;
  /** Contexto 2D donde se hace la reducción; por defecto un canvas fuera de pantalla. */
  createContext?: (side: number) => RoiContext | null;
}

function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** Canvas de trabajo de `side × side`: `OffscreenCanvas` si existe, si no uno del DOM. */
export function createOffscreenContext(side: number): RoiContext | null {
  try {
    if (typeof OffscreenCanvas === 'function') {
      const canvas = new OffscreenCanvas(side, side);
      const context = canvas.getContext('2d', { willReadFrequently: true });
      return (context as unknown as RoiContext | null) ?? null;
    }
    if (typeof document === 'undefined') {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    return (context as unknown as RoiContext | null) ?? null;
  } catch {
    return null;
  }
}

/** Programa la siguiente vuelta: un fotograma de cámara si se puede, si no uno de pintado. */
function scheduleTick(video: HTMLVideoElement, callback: () => void): () => void {
  const withVideoCallback = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number;
    cancelVideoFrameCallback?: (handle: number) => void;
  };
  if (typeof withVideoCallback.requestVideoFrameCallback === 'function') {
    const handle = withVideoCallback.requestVideoFrameCallback(callback);
    return () => withVideoCallback.cancelVideoFrameCallback?.(handle);
  }
  if (typeof globalThis.requestAnimationFrame === 'function') {
    const handle = globalThis.requestAnimationFrame(() => callback());
    return () => globalThis.cancelAnimationFrame(handle);
  }
  const handle = globalThis.setTimeout(callback, 33);
  return () => globalThis.clearTimeout(handle);
}

/**
 * Arranca el bucle. Devuelve la función que lo detiene (idempotente); **no** cierra el decodificador
 * ni la cámara: eso lo decide quien los abrió.
 */
export function startScanner(options: ScannerOptions): () => void {
  const {
    video,
    decoder,
    onRead,
    onRoi,
    now = monotonicNow,
    side = DECODER_INPUT_SIDE,
    roiFraction,
    createContext = createOffscreenContext,
  } = options;

  const controller =
    options.roiController ??
    createRoiController(roiFraction === undefined ? {} : { fixedFraction: roiFraction });

  const context = createContext(side);
  let active = true;
  let cancel: (() => void) | null = null;
  let announced: ScanRoi | null = null;

  /** Anuncia el ROI vigente solo cuando cambia de verdad: el marco no debe repintarse por nada. */
  const announce = (roi: ScanRoi): void => {
    if (
      announced !== null &&
      announced.fraction === roi.fraction &&
      announced.sidePx === roi.sidePx &&
      announced.locked === roi.locked &&
      announced.videoWidth === roi.videoWidth &&
      announced.videoHeight === roi.videoHeight
    ) {
      return;
    }
    announced = roi;
    onRoi?.(roi);
  };

  const tick = (): void => {
    if (!active) {
      return;
    }
    cancel = scheduleTick(video, tick);
    if (context === null || video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }
    const capturedAtMs = now();
    const decision = controller.decide(capturedAtMs);
    const rect = centerRoi(video.videoWidth, video.videoHeight, decision.fraction);
    announce({
      fraction: decision.fraction,
      sidePx: rect.width,
      locked: decision.locked,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
    });
    let image: ImageData;
    try {
      image = drawReducedRoi(context, video, rect, side);
    } catch {
      // Un fotograma que el canvas no pudo leer (pista en pausa, cambio de orientación) se salta.
      return;
    }
    void decoder.decode(image).then((outcome) => {
      if (!active || outcome === null || outcome.text === null) {
        return;
      }
      if (outcome.symbolSidePx !== null) {
        // El enganche del recorte se alimenta con la geometría de la lectura, medida sobre el ROI
        // **con el que se capturó**, que puede no ser ya el vigente (T096).
        controller.observe({
          symbolSidePx: outcome.symbolSidePx,
          roiFraction: decision.fraction,
          nowMs: now(),
        });
      }
      onRead({
        text: outcome.text,
        capturedAtMs,
        symbolSidePx: outcome.symbolSidePx,
        decodeMs: outcome.decodeMs,
        roiFraction: decision.fraction,
        roiSidePx: rect.width,
      });
    });
  };

  tick();

  return () => {
    if (!active) {
      return;
    }
    active = false;
    cancel?.();
    cancel = null;
  };
}
